import { GEOMETRY_TOLERANCE, type ShapePoint, type ShapeVertex } from '@repo/api-contracts';

const plain = (point: ShapePoint): ShapePoint => ({ xMm: point.xMm, yMm: point.yMm });

/** 부드러운 점으로 보는 두 손잡이 방향 단위 벡터 외적의 허용치. */
const SMOOTH_CROSS_TOLERANCE = 1e-4;

/**
 * 부드러운 점 — 손잡이 둘이 다 있고 점을 사이에 두고 한 직선 위 반대쪽에 있다. 손잡이를 끌면 반대 손잡이가
 * 대칭으로 따라온다. Alt 로 따로 꺾은 점은 손잡이가 있어도 뾰족한 점이다.
 */
export const isSmoothVertex = (vertex: ShapeVertex): boolean => {
  const { handleIn, handleOut } = vertex;
  if (handleIn === undefined || handleOut === undefined) return false;
  const ax = handleIn.xMm - vertex.xMm;
  const ay = handleIn.yMm - vertex.yMm;
  const bx = handleOut.xMm - vertex.xMm;
  const by = handleOut.yMm - vertex.yMm;
  const la = Math.hypot(ax, ay);
  const lb = Math.hypot(bx, by);
  if (la <= GEOMETRY_TOLERANCE || lb <= GEOMETRY_TOLERANCE) return false;
  // 두 방향 단위 벡터의 외적이 0 에 가깝고 내적이 음수(반대 방향)면 부드럽다. 저장 때 좌표를 1e-7mm 로 반올림하므로
  // 아주 짧은 손잡이(1e-3mm 안팎)도 대칭으로 보도록 외적 허용치를 1e-4(약 0.006°)로 둔다.
  const cross = (ax * by - ay * bx) / (la * lb);
  const dot = (ax * bx + ay * by) / (la * lb);
  return Math.abs(cross) < SMOOTH_CROSS_TOLERANCE && dot < 0;
};

/**
 * 점 더블클릭 — 손잡이가 있으면 둘 다 지워 뾰족한 점으로, 없으면 이웃 접선(앞 점 → 뒤 점 방향)을 따라 손잡이를 만든다.
 * 각 손잡이 길이는 그쪽 이웃까지 거리의 1/3 이다. 앞뒤 점이 같은 자리라 방향이 없거나 index 가 없으면 null.
 */
export const toggleVertexSmooth = (
  points: readonly ShapeVertex[],
  index: number,
): ShapeVertex[] | null => {
  const vertex = points[index];
  const previous = points[(index - 1 + points.length) % points.length];
  const next = points[(index + 1) % points.length];
  if (vertex === undefined || previous === undefined || next === undefined) return null;
  if (vertex.handleIn !== undefined || vertex.handleOut !== undefined)
    return points.map((point, position) => (position === index ? plain(point) : point));
  const dx = next.xMm - previous.xMm;
  const dy = next.yMm - previous.yMm;
  const length = Math.hypot(dx, dy);
  if (length <= GEOMETRY_TOLERANCE) return null;
  const ux = dx / length;
  const uy = dy / length;
  const toPrevious = Math.hypot(previous.xMm - vertex.xMm, previous.yMm - vertex.yMm) / 3;
  const toNext = Math.hypot(next.xMm - vertex.xMm, next.yMm - vertex.yMm) / 3;
  const smooth: ShapeVertex = {
    xMm: vertex.xMm,
    yMm: vertex.yMm,
    handleIn: { xMm: vertex.xMm - ux * toPrevious, yMm: vertex.yMm - uy * toPrevious },
    handleOut: { xMm: vertex.xMm + ux * toNext, yMm: vertex.yMm + uy * toNext },
  };
  return points.map((point, position) => (position === index ? smooth : point));
};
